import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type Chapter,
  type Lens,
  type Manifest,
  type MediaItem,
  formatRange,
  readResume,
  selectBest,
  sortItems,
  validResume,
  writeResume,
} from '@seemantham/core';
import { Home } from './components/Home';
import { ChapterView } from './components/ChapterView';
import { PhotoView } from './components/PhotoView';
import { useTheme } from './lib/useTheme';

type View =
  | { name: 'home' }
  | { name: 'chapter'; chapterId: string }
  | { name: 'person'; personId: string }
  | { name: 'item'; itemId: string };

function storage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

export function App({ manifest }: { manifest: Manifest }) {
  const { theme, toggle } = useTheme();
  const [lens, setLens] = useState<Lens>('day');
  const [view, setView] = useState<View>({ name: 'home' });
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 900);

  useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const byId = useMemo(() => new Map(manifest.items.map((item) => [item.id, item])), [manifest]);
  const resumePoints = useMemo(() => readResume(storage()), []);
  const resumeId = validResume(resumePoints, lens, byId.keys());
  const resumeItem = resumeId ? byId.get(resumeId) : undefined;

  /** The list the viewer walks with arrows or a swipe: whatever lens opened it. */
  const activeList: MediaItem[] = useMemo(() => {
    if (view.name === 'item') {
      const chapter = manifest.chapters.find((c) => c.itemIds.includes(view.itemId));
      if (lens === 'best') return selectBest(manifest.items);
      if (lens === 'people') {
        const person = manifest.people.find((p) => manifest.items.some((i) => i.id === view.itemId && i.people?.includes(p.id)));
        if (person) return sortItems(manifest.items.filter((i) => i.people?.includes(person.id)));
      }
      if (chapter) {
        return chapter.itemIds.map((id) => byId.get(id)).filter((i): i is MediaItem => Boolean(i));
      }
    }
    return sortItems(manifest.items);
  }, [byId, lens, manifest, view]);

  const openItem = useCallback(
    (item: MediaItem) => {
      setView({ name: 'item', itemId: item.id });
      writeResume({ ...resumePoints, [lens]: item.id }, storage());
    },
    [lens, resumePoints],
  );

  const move = useCallback(
    (delta: number) => {
      if (view.name !== 'item') return;
      const index = activeList.findIndex((item) => item.id === view.itemId);
      const next = activeList[index + delta];
      if (next) openItem(next);
    },
    [activeList, openItem, view],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === 'Escape') return setView({ name: 'home' });
      if (view.name === 'item') {
        if (event.key === 'ArrowRight' || event.key.toLowerCase() === 'k') return move(1);
        if (event.key === 'ArrowLeft' || event.key.toLowerCase() === 'j') return move(-1);
      }
      if (event.key === '1') setLens('day');
      if (event.key === '2') setLens('best');
      if (event.key === '3') setLens('people');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [move, view]);

  if (view.name === 'item') {
    const item = byId.get(view.itemId);
    if (item) {
      const chapter = manifest.chapters.find((c) => c.itemIds.includes(item.id));
      return (
        <PhotoView
          item={item}
          items={activeList}
          chapterLabel={chapter ? chapter.name ?? formatRange(chapter.startsAt, chapter.endsAt) : undefined}
          onBack={() => setView({ name: 'home' })}
          onMove={move}
          onShowInDay={() => setView(chapter ? { name: 'chapter', chapterId: chapter.id } : { name: 'home' })}
        />
      );
    }
  }

  if (view.name === 'chapter') {
    const chapter = manifest.chapters.find((c) => c.id === view.chapterId);
    if (chapter) {
      return (
        <ChapterView
          manifest={manifest}
          chapter={chapter}
          onBack={() => setView({ name: 'home' })}
          onOpenItem={openItem}
        />
      );
    }
  }

  if (view.name === 'person') {
    const person = manifest.people.find((p) => p.id === view.personId);
    if (person) {
      const items = sortItems(manifest.items.filter((item) => item.people?.includes(person.id)));
      const chapter: Chapter = {
        id: `person-${person.id}`,
        startsAt: items[0]?.capturedAt ?? manifest.event.startsAt,
        endsAt: items[items.length - 1]?.capturedAt ?? manifest.event.endsAt,
        name: person.name,
        photoCount: items.filter((i) => i.kind === 'photo').length,
        clipCount: items.filter((i) => i.kind === 'clip').length,
        itemIds: items.map((i) => i.id),
      };
      return (
        <ChapterView
          manifest={manifest}
          chapter={chapter}
          onBack={() => setView({ name: 'home' })}
          onOpenItem={openItem}
        />
      );
    }
  }

  return (
    <Home
      manifest={manifest}
      lens={lens}
      onLens={setLens}
      onOpenChapter={(chapter) => setView({ name: 'chapter', chapterId: chapter.id })}
      onOpenItem={openItem}
      onOpenPerson={(personId) => setView({ name: 'person', personId })}
      resumeItem={resumeItem}
      theme={theme}
      onToggleTheme={toggle}
      wide={wide}
    />
  );
}
